/* eslint-disable react/no-unescaped-entities */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight, Check, ShieldCheck, Globe, HeartHandshake, Mail,
  CalendarClock, Link2, Share2, FileWarning, Users,
  Gauge, Sparkles, ArrowUpRight, Eye, Bell,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import Header from "../components/marketing/Header";
import Footer from "../components/marketing/Footer";
import LiveDonationCounter from "../components/marketing/LiveDonationCounter";
import DashboardMockup from "../components/DashboardMockup";
import SampleReport from "../components/SampleReport";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";

function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.transitionDelay = `${delay}s`;
    const show = () => el.classList.add("in");
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { show(); io.disconnect(); }
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    io.observe(el);
    // Safety net: never leave content hidden even if observer/animation misbehaves
    const fallback = setTimeout(show, 1400);
    return () => { io.disconnect(); clearTimeout(fallback); };
  }, [delay]);
  return <div ref={ref} className={`reveal ${className}`}>{children}</div>;
}

function Overline({ children }) {
  return <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-charcoal-400 mb-4">{children}</p>;
}

const HERO_LINES = ["Know your nonprofit's website is working—", "before your donors discover it isn't."];

export default function Landing({ scrollTo }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sampleOpen, setSampleOpen] = useState(false);

  useEffect(() => {
    if (scrollTo) {
      setTimeout(() => document.getElementById(scrollTo)?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, [scrollTo]);

  const goScroll = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const choosePlan = async (plan_id) => {
    if (!user) { navigate("/register"); return; }
    if (user.subscription_status === "active") { navigate("/dashboard"); return; }
    navigate("/get-started");
  };

  const getStarted = () => {
    if (!user) { navigate("/register"); return; }
    navigate(user.subscription_status === "active" ? "/dashboard" : "/get-started");
  };

  return (
    <div id="top" className="bg-charcoal-50 overflow-x-hidden">
      <Header onSampleReport={() => setSampleOpen(true)} />
      <SampleReport open={sampleOpen} onClose={() => setSampleOpen(false)} />

      {/* ================= HERO ================= */}
      <section className="relative pt-32 sm:pt-40 pb-20 sm:pb-28">
        <div className="absolute inset-0 tech-grid tech-grid-fade pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <Reveal>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-black/5 shadow-soft mb-7">
                <span className="w-2 h-2 rounded-full bg-emerald-500 dot-pulse" />
                <span className="text-xs font-mono text-charcoal-600">Digital care for small nonprofits</span>
              </div>
            </Reveal>

            <Reveal delay={0.08}>
              <h1 className="text-5xl md:text-6xl lg:text-[4.2rem] font-semibold tracking-tighter leading-[1.04] text-charcoal-900">
                {HERO_LINES.map((line, i) => (
                  <span key={i} className="block">{line}</span>
                ))}
              </h1>
            </Reveal>

            <Reveal delay={0.16}>
              <p className="mt-6 text-base md:text-lg text-charcoal-500 leading-relaxed max-w-xl">
                Mission Watch protects your most important pages throughout the day,
                audits your website daily, and helps your team stay ahead of problems
                before they touch your donors&apos; trust.
              </p>
            </Reveal>

            <Reveal delay={0.24}>
              <div className="mt-9">
                <button onClick={getStarted}
                  className="group inline-flex items-center gap-2 px-6 h-12 rounded-full bg-brand text-white font-medium text-sm hover:bg-brand-hover transition-colors shadow-soft"
                  data-testid="hero-primary-cta">
                  See If Anything Is Broken
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </button>
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => setSampleOpen(true)}
                    className="inline-flex items-center gap-1.5 text-sm text-charcoal-500 hover:text-charcoal-900 hover:underline underline-offset-4 decoration-charcoal-300 transition-colors"
                    data-testid="hero-sample-cta"
                  >
                    <Eye className="w-3.5 h-3.5" /> See a sample report
                  </button>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="mt-4 text-sm text-charcoal-400" data-testid="hero-trial-note">
                7-day free trial · Cancel anytime before your first billing date
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <DashboardMockup />
          </Reveal>
        </div>
      </section>

      {/* ================= THREE LAYERS (Iteration 34) =================
           A visual story of the three services working together. Sits
           directly under the hero so visitors immediately understand
           what Mission Watch actually DOES for them — not what it is
           technically. Layers are ordered by cadence (most frequent
           first) so the reader's eye moves from "always watching" →
           "daily audit" → "long-term insight". */}
      <section className="pt-4 pb-8 sm:pb-14" data-testid="three-layer-section">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="text-center mb-10 sm:mb-14">
              <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-charcoal-400 mb-3">
                How Mission Watch works for you
              </p>
              <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-charcoal-900 max-w-2xl mx-auto leading-[1.2]">
                Three quiet layers of care, working together.
              </h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-4 sm:gap-5">
            {[
              {
                badge: "Throughout the day",
                title: "Mission Watch Protection",
                promise: "Protect your critical pages.",
                body: "Mission Watch watches your homepage, donation page, and contact page all day long — and gently confirms every issue before we ever wake you.",
                icon: ShieldCheck,
                testid: "layer-protection",
                dot: "bg-emerald-500",
                ring: "ring-emerald-100",
              },
              {
                badge: "Every day",
                title: "Mission Watch Audit",
                promise: "Inspect your website every day.",
                body: "A daily inspection of your whole website — broken links, donation issues, SSL, accessibility, compliance reminders — surfaced in plain language.",
                icon: Eye,
                testid: "layer-audit",
                dot: "bg-brand",
                ring: "ring-brand/15",
              },
              {
                badge: "Every week",
                title: "Mission Watch Health Report",
                promise: "Improve your website over time.",
                body: "A calm, long-term view of your website's quality, trends, performance, and health — so your board sees progress, not panic.",
                icon: Sparkles,
                testid: "layer-health-report",
                dot: "bg-amber-500",
                ring: "ring-amber-100",
              },
            ].map((L, i) => (
              <Reveal key={L.title} delay={i * 0.08}>
                <div
                  className="relative h-full rounded-2xl bg-white border border-black/5 shadow-soft p-6 sm:p-7 hover:shadow-premium hover:-translate-y-1 transition-all duration-300"
                  data-testid={L.testid}
                >
                  <div className="flex items-center gap-2 mb-5">
                    <span className={`w-2 h-2 rounded-full ${L.dot} ring-4 ${L.ring}`} />
                    <span className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-charcoal-400">
                      {L.badge}
                    </span>
                  </div>
                  <div className="w-11 h-11 rounded-xl bg-charcoal-900 grid place-items-center mb-4">
                    <L.icon className="w-5 h-5 text-emerald-400" strokeWidth={1.75} />
                  </div>
                  <h3 className="text-lg font-medium text-charcoal-900 leading-snug">
                    {L.title}
                  </h3>
                  <p className="mt-1 text-sm font-medium text-charcoal-800">
                    {L.promise}
                  </p>
                  <p className="mt-3 text-sm text-charcoal-500 leading-relaxed">
                    {L.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.28}>
            <p
              className="text-center text-sm text-charcoal-500 mt-8 max-w-2xl mx-auto"
              data-testid="three-layer-footnote"
            >
              You don&apos;t manage any of it. Mission Watch does the work — you get a
              calm signal when something needs your attention.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ================= WHY IT HAPPENS TIMELINE + TESTIMONIAL ================= */}
      {/* Moved directly under the hero so visitors believe the problem BEFORE they see features.
           Ends with a real quote so the story lands with a specific human name attached. */}
      <section className="pt-4 pb-16 sm:pb-24 bg-charcoal-50">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="rounded-2xl border border-black/10 bg-white p-7 sm:p-9 shadow-soft">
              <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-charcoal-400 mb-6">How problems go unnoticed</p>
              <div className="grid sm:grid-cols-4 gap-6">
                {[
                  ["Month 1", "Donation page silently fails after a plugin update."],
                  ["Month 2", "Donors assume the button is broken and leave."],
                  ["Month 3", "Nobody on the team notices anything is wrong."],
                  ["Month 4", "A board member finally reports lost donations."],
                ].map(([m, d], i) => (
                  <div key={i} className="relative">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`w-2.5 h-2.5 rounded-full ${i === 3 ? "bg-amber-500" : "bg-charcoal-200"}`} />
                      <span className="text-xs font-mono text-charcoal-500">{m}</span>
                    </div>
                    <p className="text-sm text-charcoal-700 leading-relaxed">{d}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <figure className="mt-8 rounded-2xl bg-charcoal-900 text-white p-7 sm:p-9 relative overflow-hidden" data-testid="testimonial-jesus-mission">
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-emerald-500/15 blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="text-emerald-400 text-4xl leading-none font-serif mb-3">&ldquo;</div>
                <blockquote className="text-lg sm:text-xl leading-[1.5] text-white/90 max-w-3xl">
                  We thought everything on our website was working. Mission Watch proved otherwise.
                  It found a broken donation page and several dead links before they impacted our
                  next fundraising campaign.
                </blockquote>
                <figcaption className="mt-6 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-emerald-500/20 border border-emerald-400/30 grid place-items-center text-emerald-300">
                    <ShieldCheck className="w-4 h-4" strokeWidth={2} />
                  </div>
                  <p className="text-xs font-mono text-white/60 uppercase tracking-[0.14em]">Beta nonprofit organization</p>
                </figcaption>
              </div>
            </figure>
          </Reveal>
        </div>
      </section>

      {/* ================= MARQUEE ================= */}
      <section className="py-8 border-y border-black/5 bg-white overflow-hidden">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <p className="text-center text-[11px] font-mono uppercase tracking-[0.2em] text-charcoal-400 mb-6">
            The systems your mission runs on — quietly cared for
          </p>
        </div>
        <div className="relative flex whitespace-nowrap">
          <div className="flex animate-marquee">
            {[...Array(2)].map((_, k) => (
              <div key={k} className="flex items-center gap-14 px-7 text-charcoal-300">
                {["WordPress", "Squarespace", "Donorbox", "Stripe", "Mailchimp", "DNS / Domains", "SSL Certificates", "Contact Forms", "Wix", "PayPal"].map((s) => (
                  <span key={s} className="text-lg font-medium tracking-tight">{s}</span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= TRUST ================= */}
      <section className="py-24 sm:py-32">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <Reveal>
            <Overline>Peace of mind</Overline>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-charcoal-900 max-w-3xl leading-[1.15]">
              Your mission deserves better than <span className="text-charcoal-400">"I hope everything still works."</span>
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-5 mt-14">
            {[
              { icon: Gauge, title: "Mission Watch Health Report", body: "A calm long-term view of your website's trends, performance, and quality — the kind of clarity you can share with your board." },
              { icon: Bell, title: "Instant Issue Alerts", body: "The moment something breaks — a down donation page, an expired certificate — Mission Watch emails you, before a donor ever notices." },
              { icon: FileWarning, title: "Plain-language Reports", body: "No dashboards to babysit. No technical jargon. Just what's working, what needs attention, and one clear next step." },
            ].map((c, i) => (
              <Reveal key={c.title} delay={i * 0.1}>
                <div className="h-full rounded-2xl bg-white border border-black/5 shadow-soft p-7 hover:shadow-premium hover:-translate-y-1 transition-all duration-300" data-testid={`trust-card-${i}`}>
                  <div className="w-11 h-11 rounded-xl bg-charcoal-900 grid place-items-center mb-5">
                    <c.icon className="w-5 h-5 text-emerald-400" strokeWidth={1.75} />
                  </div>
                  <h3 className="text-lg font-medium text-charcoal-900">{c.title}</h3>
                  <p className="text-sm text-charcoal-500 mt-2.5 leading-relaxed">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= PROBLEM ================= */}
      <section className="py-24 sm:py-32 bg-charcoal-900 text-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-14">
            <Reveal>
              <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-emerald-400 mb-4">01 — The reality</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight leading-[1.12]">
                Small nonprofits wear too many hats.
              </h2>
              <p className="mt-6 text-white/60 leading-relaxed max-w-lg">
                Executive Directors quietly become the IT department, marketing team, bookkeeper, fundraiser,
                volunteer coordinator, and operations manager—all at once. Because of this, important things
                quietly break, and nobody notices for months.
              </p>
              <div className="mt-8 flex flex-wrap gap-2">
                {["IT department", "Marketing", "Bookkeeper", "Fundraiser", "Volunteer coordinator", "Operations"].map((r) => (
                  <span key={r} className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-white/70">{r}</span>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: HeartHandshake, t: "Broken donation page" },
                  { icon: Mail, t: "Contact form failure" },
                  { icon: ShieldCheck, t: "Expired SSL certificate" },
                  { icon: Globe, t: "Outdated website" },
                  { icon: CalendarClock, t: "Domain expiration" },
                  { icon: FileWarning, t: "Missed annual reminder" },
                  { icon: Link2, t: "Broken social links" },
                  { icon: Users, t: "Old board information" },
                ].map((x, i) => (
                  <div key={i} className="flex items-center gap-3 rounded-xl bg-white/5 border border-white/10 px-4 py-3.5">
                    <x.icon className="w-4 h-4 text-white/40 shrink-0" strokeWidth={1.75} />
                    <span className="text-sm text-white/80">{x.t}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ================= HOW IT WORKS ================= */}
      <section id="how" className="py-24 sm:py-32">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <Reveal>
            <Overline>02 — How it works</Overline>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-charcoal-900">Three simple steps.</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 mt-14 border border-black/5 rounded-2xl bg-white overflow-hidden shadow-soft">
            {[
              { n: "01", t: "Sign Up", d: "Tell us your website, donation page, and important dates. Setup takes about a minute." },
              { n: "02", t: "Mission Watch Takes Over", d: "Protection watches your critical pages all day. Audit inspects your whole site daily. You don't manage anything." },
              { n: "03", t: "Calm, Plain-language Signals", d: "When something needs your attention, you get one clear email. When your Health Report is ready, it lands in your inbox." },
            ].map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1}>
                <div className={`p-8 h-full ${i < 2 ? "md:border-r border-black/5" : ""} border-b md:border-b-0 border-black/5`}>
                  <span className="text-4xl font-semibold tracking-tighter text-charcoal-100">{s.n}</span>
                  <h3 className="text-xl font-medium text-charcoal-900 mt-4">{s.t}</h3>
                  <p className="text-sm text-charcoal-500 mt-2.5 leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= HOURLY DONATION-FORM PROMISE (Complete-plan headline) ================= */}
      {/* The single feature every ED cares about — placed right before the feature list so
           it lands before they start skimming. Doubles as an upsell hook for Complete. */}
      <section className="relative py-24 sm:py-32 bg-charcoal-900 text-white overflow-hidden" data-testid="donation-promise-section">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[520px] h-[520px] rounded-full bg-emerald-500/10 blur-[120px]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full border border-emerald-400/10" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full border border-emerald-400/5" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[36rem] h-[36rem] rounded-full border border-emerald-400/[0.03]" />
        </div>
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/25 mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 dot-pulse" />
              <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-emerald-300">Protection plan</span>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="text-4xl sm:text-5xl lg:text-[3.75rem] font-semibold tracking-tighter leading-[1.05]">
              Every donation form on your site—<span className="text-emerald-400">protected all day.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-7 max-w-2xl mx-auto text-lg text-white/60 leading-relaxed">
              While you&apos;re sleeping, in meetings, or fundraising — Mission Watch
              is quietly watching every donation link, form, and page across your
              entire site. If a donor can&apos;t give, you&apos;ll know before they do.
            </p>
          </Reveal>
          <Reveal delay={0.20}>
            <div className="mt-8 flex justify-center">
              <LiveDonationCounter />
            </div>
          </Reveal>
          <Reveal delay={0.24}>
            <div className="mt-12 grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto text-left">
              {[
                { n: "60", u: "min", l: "between check-ins", d: "Mission Watch Protection quietly checks your donation pages throughout the day — even on weekends and holidays." },
                { n: "28+", u: "", l: "donation platforms tracked", d: "Donorbox, Classy, GiveLively, Every.org, Stripe, PayPal, Zeffy, and more." },
                { n: "0", u: "", l: "dashboards to babysit", d: "We watch. When something needs your attention, an email arrives — that's it." },
              ].map((s) => (
                <div key={s.l} className="rounded-2xl bg-white/[0.03] border border-white/10 p-5">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-semibold tracking-tight text-white">{s.n}</span>
                    {s.u && <span className="text-sm font-mono text-white/40">{s.u}</span>}
                  </div>
                  <p className="mt-1 text-[13px] font-medium text-emerald-300">{s.l}</p>
                  <p className="mt-2.5 text-sm text-white/50 leading-relaxed">{s.d}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.32}>
            <button onClick={() => choosePlan("complete")}
              className="mt-12 group inline-flex items-center gap-2 px-7 h-12 rounded-full bg-white text-charcoal-900 font-medium text-sm hover:bg-emerald-50 transition-colors shadow-soft"
              data-testid="donation-promise-cta">
              Turn on Protection
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
            <p className="mt-3 text-xs font-mono text-white/40">7-day free trial · Cancel anytime before your first billing date</p>
          </Reveal>
        </div>
      </section>

      {/* ================= WHAT'S INCLUDED ================= */}
      <section id="included" className="py-24 sm:py-32 bg-white border-y border-black/5">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <Reveal>
            <Overline>03 — What's included</Overline>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-charcoal-900 max-w-2xl leading-[1.15]">
              Every month we check the things that actually matter.
            </h2>
            <p className="mt-4 text-charcoal-500 max-w-xl">An operational monthly checkup — <span className="text-charcoal-800 font-medium">not legal advice or accounting services.</span></p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-14">
            {[
              [Globe, "Website works"],
              [HeartHandshake, "Donations work"],
              [Mail, "Contact forms work"],
              [ShieldCheck, "Security certificates"],
              [Link2, "Broken links"],
              [CalendarClock, "Important renewal dates"],
            ].map(([Icon, label], i) => (
              <Reveal key={label} delay={(i % 3) * 0.05}>
                <div className="flex items-center gap-3 rounded-xl border border-black/5 bg-charcoal-50/60 px-4 py-4 hover:bg-white hover:shadow-soft transition-all" data-testid={`feature-${i}`}>
                  <div className="w-9 h-9 rounded-lg bg-white border border-black/5 grid place-items-center shrink-0">
                    <Icon className="w-4 h-4 text-brand" strokeWidth={1.75} />
                  </div>
                  <span className="text-sm font-medium text-charcoal-900">{label}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-8 text-sm text-charcoal-500 text-center" data-testid="feature-more-note">
              ...plus 20+ additional operational checks running quietly behind the scenes.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ================= INSTANT ALERTS CALLOUT ================= */}
      <section className="py-24 sm:py-32 bg-white border-b border-black/5">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-100 mb-6">
              <Bell className="w-3.5 h-3.5 text-emerald-600" strokeWidth={2} />
              <span className="text-xs font-mono text-emerald-700">Real-time protection</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-charcoal-900 leading-[1.12]">
              The moment something breaks, you'll know.
            </h2>
            <p className="mt-5 text-charcoal-500 leading-relaxed max-w-lg">
              We don't just check once a month. Throughout the month we automatically re-test your most important
              assets—and the instant a check goes from healthy to broken, we email you right away with what happened
              and what to do. No dashboards to watch. No logging in.
            </p>
            <div className="mt-7 flex flex-wrap gap-2">
              {["Donation page down", "SSL certificate expired", "Website offline", "Domain expiring", "Contact form broken"].map((t) => (
                <span key={t} className="px-3 py-1.5 rounded-full bg-charcoal-50 border border-black/5 text-sm text-charcoal-600">{t}</span>
              ))}
            </div>
            <p className="mt-5 text-sm text-charcoal-400">
              Re-checked automatically every hour on the <span className="text-charcoal-700 font-medium">Protection plan</span>.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-2xl bg-white border border-black/5 shadow-premium overflow-hidden max-w-md mx-auto">
              <div className="h-1.5 bg-red-500" />
              <div className="p-6">
                <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-red-50 border border-red-100 mb-4">
                  <span className="w-2 h-2 rounded-full bg-red-500 dot-pulse" />
                  <span className="text-[11px] font-mono font-medium text-red-600">Action Needed</span>
                </div>
                <p className="text-xs font-mono text-charcoal-400">from Mission Watch · just now</p>
                <h3 className="text-lg font-medium text-charcoal-900 mt-1">We spotted a problem with your donation page</h3>
                <div className="mt-4 rounded-xl border border-red-100 bg-red-50/40 p-4">
                  <p className="text-sm font-medium text-red-700">Donation Page Not Working</p>
                  <p className="text-[11px] font-mono text-charcoal-400 mt-1">Page returned a 500 error</p>
                  <p className="text-sm text-charcoal-600 mt-2 leading-relaxed">Donors may not be able to give right now. Test the page yourself and reach out to your donation provider.</p>
                </div>
                <div className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-charcoal-900">
                  <span className="px-4 py-2 rounded-full bg-charcoal-900 text-white">View your dashboard</span>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ================= SAMPLE REPORT CTA ================= */}
      <section className="py-24 sm:py-32">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="relative rounded-3xl bg-charcoal-900 text-white p-10 sm:p-16 overflow-hidden">
              <div className="absolute inset-0 tech-grid opacity-[0.15] pointer-events-none" />
              <div className="relative">
                <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-emerald-400 mb-4">Your highest-value deliverable</p>
                <h2 className="text-3xl md:text-4xl font-medium tracking-tight max-w-2xl leading-[1.15]">
                  See exactly what lands in your inbox every month.
                </h2>
                <p className="mt-4 text-white/60 max-w-xl leading-relaxed">
                  Clear, human-readable, and reassuring. Green checks where things are healthy, gentle flags where
                  they need attention, and your upcoming reminders—all in one place.
                </p>
                <button onClick={() => setSampleOpen(true)}
                  className="mt-8 group inline-flex items-center gap-2 px-6 h-12 rounded-full bg-white text-charcoal-900 font-medium text-sm hover:bg-charcoal-100 transition-colors"
                  data-testid="section-sample-cta">
                  <Eye className="w-4 h-4" /> View Sample Report
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ================= PRICING ================= */}
      <section id="pricing" className="py-24 sm:py-32 bg-white border-y border-black/5">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <Reveal>
            <Overline>Pricing</Overline>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-charcoal-900">A progression of care. <span className="text-charcoal-500">Start with an audit — add protection when you&apos;re ready.</span></h2>
            <p className="mt-4 text-charcoal-500">7-day free trial on both plans. Cancel anytime. No setup fees. No contracts.</p>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-5 mt-14">
            <Reveal>
              <div className="h-full rounded-2xl bg-white border border-black/10 shadow-soft p-8 flex flex-col" data-testid="pricing-essential">
                <p className="text-sm font-medium text-charcoal-500">Monthly Checkup</p>
                <p className="mt-1 text-[13px] text-charcoal-500 leading-relaxed">Mission Watch Audit + Health Report for small nonprofits getting started.</p>
                <div className="mt-4 flex items-end gap-1"><span className="text-5xl font-semibold tracking-tighter text-charcoal-900">$19</span><span className="text-charcoal-400 mb-1.5 text-sm">/month</span></div>
                <ul className="mt-6 space-y-3 flex-1">
                  {[
                    { section: "Mission Watch Audit", items: [
                      "Monthly website audit",
                      "Donation page review",
                      "Contact form check",
                      "SSL & domain review",
                    ]},
                    { section: "Mission Watch Health Report", items: [
                      "Monthly Health Report emailed to you",
                      "Up to 5 reminder dates",
                    ]},
                    { section: "Alerts", items: [
                      "Instant email alerts for issues found during your monthly audit",
                    ]},
                  ].map((group) => (
                    <li key={group.section} className="space-y-1.5">
                      <p className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-charcoal-400">{group.section}</p>
                      {group.items.map((f) => (
                        <div key={f} className="flex items-start gap-2.5 text-sm text-charcoal-700"><Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" strokeWidth={2.25} />{f}</div>
                      ))}
                    </li>
                  ))}
                </ul>
                <button onClick={() => choosePlan("essential")}
                  className="mt-8 h-12 rounded-full bg-charcoal-900 text-white font-medium text-sm hover:bg-charcoal-800 transition-colors"
                  data-testid="choose-essential-btn">
                  Start My Free Checkup
                </button>
                <p className="mt-3 text-center text-xs text-charcoal-400">Free for 7 days, then $19/month. Cancel anytime.</p>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="relative h-full rounded-2xl bg-charcoal-900 text-white p-8 flex flex-col shadow-premium ring-1 ring-brand/40" data-testid="pricing-complete">
                <div className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-brand text-white text-[11px] font-mono uppercase tracking-wider">Most Popular</div>
                <p className="text-sm font-medium text-white/70">Protection</p>
                <p className="mt-1 text-[13px] text-white/60 leading-relaxed">The complete Mission Watch experience — Protection + Audit + Health Report.</p>
                <div className="mt-4 flex items-end gap-1"><span className="text-5xl font-semibold tracking-tighter">$39</span><span className="text-white/40 mb-1.5 text-sm">/month</span></div>
                <div className="mt-4 rounded-xl bg-emerald-500/10 border border-emerald-500/25 px-4 py-3">
                  <p className="text-sm text-white leading-snug">
                    <span className="text-emerald-300 font-semibold">Your donation pages are protected throughout the day</span> — even on weekends and holidays.
                  </p>
                </div>
                <ul className="mt-5 space-y-3 flex-1">
                  {[
                    { section: "Mission Watch Protection", items: [
                      "Every donation form protected throughout the day",
                      "Your website checked throughout the day",
                      "Contact forms checked throughout the day",
                      "Alerts sent within the hour",
                    ]},
                    { section: "Mission Watch Audit", items: [
                      "Everything in Monthly Checkup",
                      "Whole-site coverage — up to 150 pages",
                      "Broken-link monitoring",
                    ]},
                    { section: "Mission Watch Health Report", items: [
                      "Mobile experience audit — mobile & tablet",
                      "Unlimited reminder dates",
                      "Public verification badge",
                    ]},
                    { section: "Extras", items: [
                      "Multiple team members",
                      "Priority support",
                    ]},
                  ].map((group) => (
                    <li key={group.section} className="space-y-1.5">
                      <p className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-emerald-300/70">{group.section}</p>
                      {group.items.map((f) => (
                        <div key={f} className="flex items-start gap-2.5 text-sm text-white/85">
                          <Check className="w-4 h-4 mt-0.5 shrink-0 text-emerald-400" strokeWidth={2.25} />
                          <span>{f}</span>
                        </div>
                      ))}
                    </li>
                  ))}
                </ul>
                <button onClick={() => choosePlan("complete")}
                  className="mt-8 h-12 rounded-full bg-brand text-white font-medium text-sm hover:bg-brand-hover transition-colors"
                  data-testid="choose-complete-btn">
                  Start Protecting My Nonprofit
                </button>
                <p className="mt-3 text-center text-xs text-white/40">Free for 7 days, then $39/month. Cancel anytime.</p>
              </div>
            </Reveal>
          </div>

          {/* Complete+ — custom-quote tier for larger, growing nonprofits. Positioned
              as the natural next step, not a hard upsell. Fires an in-app milestone
              banner + celebratory email only after a customer's site has consistently
              exceeded the Complete cap for ≥30 scans AND ≥7 days. */}
          <Reveal delay={0.15}>
            <div
              className="mt-10 rounded-2xl border border-charcoal-200 bg-charcoal-50/60 p-6 sm:p-8"
              data-testid="complete-plus-marketing"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="max-w-2xl">
                  <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-brand mb-1">Complete+</p>
                  <h3 className="text-lg sm:text-xl font-medium text-charcoal-900 leading-snug">
                    Larger organization?
                  </h3>
                  <p className="mt-2 text-sm text-charcoal-600 leading-relaxed">
                    If your nonprofit has <span className="font-medium text-charcoal-800">more than 150 pages</span>,
                    multiple websites, or complex donation systems, we've designed Complete+ for the next stage of your growth.
                    <span className="font-medium text-charcoal-800"> Starting at $99/month.</span>
                  </p>
                </div>
                <a
                  href="#contact"
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-charcoal-900 text-white text-sm font-medium hover:bg-charcoal-800 transition-colors shadow-soft whitespace-nowrap"
                  data-testid="complete-plus-contact-btn"
                >
                  Talk to us about Complete+
                </a>
              </div>
              <ul className="mt-5 grid sm:grid-cols-2 gap-y-2 gap-x-6 text-sm text-charcoal-700">
                {[
                  "Full-site monitoring on every scan",
                  "Designed for larger organizations",
                  "Multiple websites and subdomains",
                  "Expanded donation platform monitoring",
                  "Up to 10 team members",
                  "Priority support",
                  "Advanced monitoring configuration",
                  "Quarterly Digital Health Review",
                  "Everything included in Complete",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          {/* Comparison table — makes the upgrade difference obvious. */}
          <Reveal delay={0.2}>
            <div className="mt-20" data-testid="pricing-compare-table">
              <div className="rounded-2xl border border-black/10 bg-white overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-black/5">
                      <th className="text-left px-5 py-3.5 font-medium text-charcoal-900 w-1/2">Feature</th>
                      <th className="text-center px-5 py-3.5 font-medium text-charcoal-900">Monthly Checkup</th>
                      <th className="text-center px-5 py-3.5 font-medium text-brand">Protection</th>
                    </tr>
                  </thead>
                  <tbody className="[&>tr:not(:last-child)]:border-b [&>tr]:border-black/5">
                    {[
                      ["Monthly operations checkup", "Yes", "Yes"],
                      ["Monthly health report", "Yes", "Yes"],
                      ["Website review", "Monthly", "Hourly"],
                      ["Donation-page checks", "Monthly", "Hourly"],
                      ["Contact-form checks", "Monthly", "Hourly"],
                      ["SSL and domain checks", "Monthly", "Hourly where applicable"],
                      ["Alerts", "After monthly check", "Within the hour"],
                      ["Pages covered", "Core pages", "Up to 150 pages"],
                      ["Reminder dates", "Up to 5", "Unlimited"],
                      ["Team members", "1", "Multiple"],
                      ["Public verification badge", "No", "Yes"],
                      ["Mobile Audit (mobile & tablet)", "No", "Yes (daily)"],
                      ["Priority support", "No", "Yes"],
                    ].map(([label, chk, prot], i) => {
                      const chkYes = chk === "Yes";
                      const chkNo = chk === "No";
                      const protYes = prot === "Yes";
                      return (
                        <tr key={i}>
                          <td className="px-5 py-3 text-charcoal-800">{label}</td>
                          <td className={`text-center px-5 py-3 ${chkNo ? "text-charcoal-400" : "text-charcoal-700"}`}>{chk}</td>
                          <td className={`text-center px-5 py-3 font-medium ${protYes ? "text-emerald-600" : "text-charcoal-900"}`}>{prot}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ================= FAQ ================= */}
      <section id="faq" className="py-24 sm:py-32">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Reveal>
            <Overline>Questions</Overline>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-charcoal-900">Frequently asked.</h2>
          </Reveal>
          <Reveal delay={0.05}>
            <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
              {[
                ["What exactly do you check?", "Your website availability, donation page, contact forms, SSL certificate, domain status, broken links, social media links, and key pages like your board, staff, privacy, terms, and mission statement."],
                ["Do I need to install anything?", "No. Just share your website and important URLs. There is nothing to install and no code to add."],
                ["How long does a checkup take?", "We handle it for you each month. You simply receive a clear report—no work required on your end."],
                ["Is this automated?", "Both. We automatically re-check your site throughout the month—so we can alert you the moment something breaks—and we add a human review to your monthly report so it reflects real, practical judgment, not just a script."],
                ["Can I cancel anytime?", "Yes. There are no contracts and no cancellation fees. Cancel whenever you'd like."],
                ["Do you provide legal or tax advice?", "No. Mission Watch is an operational checkup only. We do not provide legal, tax, or accounting advice."],
                ["What if you find something broken?", "We flag it clearly in your report with a plain-language explanation and a recommended action. And if something important breaks between monthly checkups, we email you an instant alert right away—so you can fix it before it costs you donations."],
                ["How are reminders handled?", "You add important dates (insurance, registration, board meetings) and we surface them in your monthly report before they're due."],
                ["Can I add multiple people to receive reports?", "Yes—multiple team members are included on the Protection plan."],
              ].map(([q, a], i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-black/5" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left text-charcoal-900 hover:no-underline text-base font-medium py-5">{q}</AccordionTrigger>
                  <AccordionContent className="text-charcoal-500 text-sm leading-relaxed pb-5">{a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* ================= ABOUT ================= */}
      <section id="about" className="py-24 sm:py-32 bg-white border-y border-black/5">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Reveal>
            <Overline>About</Overline>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-charcoal-900 leading-[1.15]">
              Built for nonprofits that can't afford to miss something important.
            </h2>
            <div className="mt-6 space-y-4 text-charcoal-500 leading-relaxed">
              <p>Many small organizations don't have an operations department. There's no IT person, no ops manager—just a small, dedicated team already stretched thin doing meaningful work.</p>
              <p>Mission Watch exists to give small nonprofits the confidence that their digital presence is healthy every month. We watch the little things quietly in the background, so the people doing important work never have to wonder if something slipped through the cracks.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ================= FINAL CTA ================= */}
      <section className="py-24 sm:py-32">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <Sparkles className="w-8 h-8 text-brand mx-auto mb-6" strokeWidth={1.5} />
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter text-charcoal-900 leading-[1.05]">
              We'll make sure your website isn't quietly working against you.
            </h2>
            <p className="mt-5 text-charcoal-500 text-lg max-w-xl mx-auto">
              Spend your time changing lives. Mission Watch will keep an eye on the little things.
            </p>
            <button onClick={getStarted}
              className="mt-9 group inline-flex items-center gap-2 px-7 h-12 rounded-full bg-brand text-white font-medium text-sm hover:bg-brand-hover transition-colors shadow-soft"
              data-testid="final-cta">
              Check My Nonprofit
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </Reveal>
        </div>
      </section>

      {/* ================= CONTACT ================= */}
      <ContactSection />

      <Footer onSampleReport={() => setSampleOpen(true)} onScrollTo={goScroll} />
    </div>
  );
}

function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks — we'll be in touch shortly.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSending(false); }
  };
  return (
    <section id="contact" className="py-24 sm:py-32 bg-white border-t border-black/5">
      <div className="max-w-2xl mx-auto px-5 sm:px-8">
        <Reveal>
          <div id="legal" />
          <Overline>Contact</Overline>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-charcoal-900">Have a question first?</h2>
          <p className="mt-3 text-charcoal-500">Send us a note and a real person will reply.</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <input required value={form.name} onChange={set("name")} placeholder="Your name"
                className="h-11 px-3.5 rounded-xl border border-black/10 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition" data-testid="contact-name" />
              <input required type="email" value={form.email} onChange={set("email")} placeholder="Email"
                className="h-11 px-3.5 rounded-xl border border-black/10 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition" data-testid="contact-email" />
            </div>
            <textarea required rows={4} value={form.message} onChange={set("message")} placeholder="How can we help?"
              className="w-full px-3.5 py-3 rounded-xl border border-black/10 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition resize-none" data-testid="contact-message" />
            <button type="submit" disabled={sending}
              className="h-11 px-6 rounded-full bg-charcoal-900 text-white text-sm font-medium hover:bg-charcoal-800 transition-colors disabled:opacity-60" data-testid="contact-submit">
              {sending ? "Sending…" : "Send message"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
